<template>
  <div class="full-width">
    <q-toolbar class="bg-dark">
      <div class="q-gutter-sm q-ml-sm">
        <q-btn
          square
          dense
          :icon="mdiMenu"
          outline
          class="q-pa-sm border-radius-10"
          @click="toggleDrawer"
          aria-label="menu"
        />
        <q-btn
          square
          dense
          :icon="mdiArrowLeft"
          outline
          size="md"
          class="q-pa-sm border-radius-10"
          @click="router.back()"
          aria-label="back"
        />
        <q-btn
          square
          dense
          :icon="mdiArrowRight"
          outline
          class="q-pa-sm border-radius-10"
          @click="router.go(1)"
          aria-label="forward"
        />
      </div>

      <q-toolbar-title>
        <div class="full-width">
          <q-input
            v-model="keyword"
            standout
            dense
            input-class="search-input text-black border-radius-10"
            :placeholder="t('search_product')"
            bg-color="white"
            @update:model-value="handleSearch"
          >
            <template #append>
              <q-btn
                square
                :icon="mdiMagnify"
                class="q-pa-sm border-radius-10 text-white bg-primary"
                @click="handleSearch"
                aria-label="search"
              />
            </template>
          </q-input>
        </div>
      </q-toolbar-title>

      <div
        class="bg-primary column justify-center items-center q-px-md q-py-sm cursor-pointer"
        @click="router.push({ name: NAIL_SUPPLY_ROUTE_NAME.CART })"
      >
        <q-icon :name="mdiCart" size="sm" />
        <div class="text-weight-bold">({{ cartItemCount }})</div>
      </div>
    </q-toolbar>
    <div
      v-if="results?.docs.length"
      class="full-width bg-white q-pa-sm shadow-2 rounded-borders"
      style="
        position: absolute;
        z-index: 1;
        max-height: calc(100vh - 150px);
        overflow-y: auto;
      "
    >
      <q-list>
        <template v-for="(item, index) in results.docs" :key="index">
          <q-item
            clickable
            @click="gotoProduct(item.category_slug, item.category_id)"
            aria-label="product"
          >
            <q-item-section avatar>
              <div class="col-12 image-wrapper">
                <q-img
                  :src="getImageFullPath(item.thumbnail)"
                  class="rounded-img"
                  :ratio="1"
                  fit="cover"
                />
              </div>
            </q-item-section>
            <q-item-section>
              <q-item-label caption>
                <div v-html="item.name"></div>
              </q-item-label>
              <q-item-label caption>{{ item.category_name }}</q-item-label>
            </q-item-section>
          </q-item>
          <q-separator v-if="index < results.docs.length - 1" />
        </template>
      </q-list>
    </div>
    <div v-for="(row, key) in chunkedHeaders" :key="key">
      <div class="row">
        <div
          v-for="(header, index) in row"
          :key="index"
          class="col-4 row no-wrap"
        >
          <q-btn
            flat
            outlined
            :color="header.color"
            class="text-caption no-wrap q-px-none q-py-sm full-width"
            no-caps
            @click="header.function"
            aria-label="header"
          >
            <template #default>
              <q-icon :name="header.icon" size="xs" class="q-mr-xs" />
              <span class="text-weight-medium">{{ header.label }}</span>
            </template>
          </q-btn>
          <q-separator size="100%" color="grey-4" style="width: 3px" />
        </div>
      </div>
      <q-separator size="3px" color="grey-4" />
    </div>
    <change-language v-model="dialog" />
  </div>
</template>
<script setup lang="ts">
import { useBase } from "@/composables/useBase";
import { useLang } from "@/composables/useLang";
import { useSearch } from "@/composables/useSearch";
import { useCartStore } from "@/stores/cartStore";
import ChangeLanguage from "@/components/popup/ChangeLanguage.vue";
import type { BaseResponsePagination } from "@/types/models";
import { computed, ref } from "vue";
import type { ProductSearch } from "@/types/product";
import { useAppStore } from "@/stores/appStore";
import {
  ACCOUNT_ROUTE_NAME,
  NAIL_SUPPLY_ROUTE_NAME,
  PROMOTION_ROUTE_NAME
} from "@/libs/route-name";
import { getImageFullPath } from "@/utils/appUtil";
import { useDebounceFn } from "@vueuse/core";
import {
  mdiMenu,
  mdiArrowLeft,
  mdiArrowRight,
  mdiMagnify,
  mdiCart,
  mdiTimerOutline,
  mdiCashSync,
  mdiSaleOutline,
  mdiPhoneOutline,
  mdiTranslate,
  mdiMessageDraw
} from "@quasar/extras/mdi-v7";

const { router } = useBase();
const { t } = useLang();
const keyword = ref("");
const dialog = ref(false);
const { searchProduct } = useSearch();
const cartStore = useCartStore();
const results = ref<BaseResponsePagination<ProductSearch> | null>(null);
const appStore = useAppStore();

const systemInfo = computed(() => {
  return appStore.feeConfiguration;
});

const headers = [
  {
    label: t("app.turn_tracker"),
    icon: mdiTimerOutline,
    color: "secondary",
    function: () => gotoPromotion()
  },
  {
    label: t("page.account.affiliate"),
    icon: mdiCashSync,
    color: "secondary",
    function: () => gotoAffiliate()
  },
  {
    label: t("app.super_sale_today"),
    icon: mdiSaleOutline,
    color: "primary",
    function: () => gotoPromotion()
  },
  {
    label: systemInfo.value.phoneSupport,
    icon: mdiPhoneOutline,
    color: "grey-8",
    function: () => {}
  },
  {
    label: t("page.account.language"),
    icon: mdiTranslate,
    color: "grey-8",
    function: () => handleLanguage()
  },
  {
    label: t("app.google_review_services"),
    icon: mdiMessageDraw,
    color: "grey-8",
    function: () => {}
  }
];

const isCheckAccountRoute = computed(() => {
  const routeName = router.currentRoute.value.name;
  return (
    typeof routeName === "string" &&
    routeName.startsWith(ACCOUNT_ROUTE_NAME.ACCOUNT)
  );
});

const chunkedHeaders = computed(() => {
  const chunkSize = 3;
  if (!isCheckAccountRoute.value) {
    return [headers.slice(0, 3)];
  }
  const result = [];
  for (let i = 0; i < headers.length; i += chunkSize) {
    result.push(headers.slice(i, i + chunkSize));
  }
  return result;
});

const handleLanguage = () => {
  dialog.value = true;
};

const cartItemCount = computed(() => {
  return cartStore.getCurrentCart?.cart?.total_quantity || 0;
});

const emit = defineEmits(["toggleDrawer"]);

const toggleDrawer = () => {
  emit("toggleDrawer");
};

const performSearch = async () => {
  if (!keyword.value.trim()) {
    results.value = null;
    return;
  }

  const response = await searchProduct(1, 12, keyword.value);
  if (response) results.value = response;
};

const handleSearch = useDebounceFn(performSearch, 1000);

const gotoProduct = (slug: string, catId: string) => {
  router.push({
    name: NAIL_SUPPLY_ROUTE_NAME.PRODUCT,
    params: { category: slug },
    query: { catId }
  });
  if (results.value) results.value.docs = [];
  keyword.value = "";
};

const gotoAffiliate = () => {
  router.push({
    name: ACCOUNT_ROUTE_NAME.AFFILIATE
  });
};

const gotoPromotion = () => {
  router.push({
    name: PROMOTION_ROUTE_NAME.PROMOTION
  });
};
</script>
<style scoped>
.q-toolbar {
  padding: 0;
}

:deep(.q-field--float) {
  .q-field__label {
    display: none;
  }
}

.search-input {
  font-size: 16px !important;
}

:deep(.q-field__native) {
  font-size: 16px !important;
}

:deep(.q-field--dense .q-field__control, .q-field--dense .q-field__marginal) {
  padding-right: 0 !important;
}

.image-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rounded-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
:deep(.q-btn--rectangle) {
  border-radius: 8px;
}
</style>
